import * as React from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { authClient } from '../../lib/auth-client';
import { FrontendErrorCode, extractErrorCode, mapBetterAuthSlug, otpErrorMessage } from '../../lib/errorCodes';
import { OTP_LENGTH, PASSWORD_MIN_LENGTH, ResetPasswordFormSchema, type ResetPasswordForm } from '../../schemas';
import { getFieldError, validateForm } from '../../schemas/forms';
import { Card, SignInContainer } from './LoginPage.styles';

const REDIRECT_DELAY_MS = 2500;

class ResetPasswordError extends Error {
	readonly slug: string | undefined;
	readonly status: number | undefined;

	constructor(message: string, slug: string | undefined, status: number | undefined) {
		super(message);
		this.slug = slug;
		this.status = status;
	}
}

function describeResetError(error: Error): string {
	if (!(error instanceof ResetPasswordError)) {
		return `Đặt lại mật khẩu thất bại. Mã lỗi E-${extractErrorCode(error)}`;
	}
	const otpMessage = otpErrorMessage(error.slug);
	if (otpMessage) return otpMessage;
	if (error.status === 429) return 'Bạn đã thao tác quá nhiều lần. Vui lòng thử lại sau.';
	const code = error.slug && /^[0-9]{5}$/.test(error.slug)
		? error.slug
		: mapBetterAuthSlug(error.slug) ?? FrontendErrorCode.UNMAPPED_SLUG;
	return `Đặt lại mật khẩu thất bại. Mã lỗi E-${code}`;
}

export default function ResetPasswordPage() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	// Prefill only: mail scanners prefetch the link, so submitting automatically would burn the code.
	const [formData, setFormData] = React.useState<ResetPasswordForm>(() => ({
		email: searchParams.get('email')?.trim() ?? '',
		otp: searchParams.get('otp')?.trim() ?? '',
		password: '',
		confirmPassword: '',
	}));
	const [validationErrors, setValidationErrors] = React.useState<z.ZodError | null>(null);
	const [showPassword, setShowPassword] = React.useState(false);

	const resetMutation = useMutation({
		mutationFn: async ({ email, otp, password }: ResetPasswordForm) => {
			const response = await authClient.emailOtp.resetPassword({ email, otp, password });
			if (response.error) {
				const { code, message, status } = response.error as { code?: unknown; message?: string; status?: number };
				throw new ResetPasswordError(
					message || 'Reset password failed',
					typeof code === 'string' ? code : undefined,
					status,
				);
			}
			return response.data;
		},
	});

	const succeeded = resetMutation.isSuccess;
	React.useEffect(() => {
		if (!succeeded) return;
		const timer = setTimeout(() => navigate('/login', { replace: true }), REDIRECT_DELAY_MS);
		return () => clearTimeout(timer);
	}, [succeeded, navigate]);

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const validation = validateForm(ResetPasswordFormSchema, formData);
		if (!validation.success || !validation.data) {
			setValidationErrors(validation.errors ?? null);
			return;
		}
		setValidationErrors(null);
		resetMutation.mutate(validation.data);
	};

	const handleChange = (field: keyof ResetPasswordForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (validationErrors) setValidationErrors(null);
	};

	const fieldError = (field: keyof ResetPasswordForm) => getFieldError(validationErrors, field);

	const passwordAdornment = (
		<InputAdornment position="end">
			<IconButton
				aria-label="Hiện/ẩn mật khẩu"
				onClick={() => setShowPassword((prev) => !prev)}
				edge="end"
				size="small"
			>
				{showPassword ? <VisibilityOff /> : <Visibility />}
			</IconButton>
		</InputAdornment>
	);

	return (
		<>
			<CssBaseline enableColorScheme />
			<SignInContainer direction="column" justifyContent="center">
				<Card variant="outlined">
					<Typography component="h1" variant="h5" sx={{ textAlign: 'center' }}>
						Đặt lại mật khẩu
					</Typography>

					{succeeded ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center' }}>
							<Alert severity="success">
								Đặt lại mật khẩu thành công. Đang chuyển về trang đăng nhập...
							</Alert>
							<Link component={RouterLink} to="/login" replace variant="body2">
								Đăng nhập ngay
							</Link>
						</Box>
					) : (
						<Box
							component="form"
							onSubmit={handleSubmit}
							noValidate
							sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
						>
							{resetMutation.error && (
								<Alert severity="error">{describeResetError(resetMutation.error)}</Alert>
							)}
							<FormControl>
								<FormLabel htmlFor="email">Email</FormLabel>
								<TextField
									id="email"
									name="email"
									type="email"
									autoComplete="email"
									required
									fullWidth
									value={formData.email}
									onChange={handleChange('email')}
									error={!!fieldError('email')}
									helperText={fieldError('email')}
								/>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="otp">Mã xác nhận</FormLabel>
								<TextField
									id="otp"
									name="otp"
									autoComplete="one-time-code"
									required
									fullWidth
									placeholder={'0'.repeat(OTP_LENGTH)}
									inputProps={{ inputMode: 'numeric', maxLength: OTP_LENGTH }}
									value={formData.otp}
									onChange={handleChange('otp')}
									error={!!fieldError('otp')}
									helperText={fieldError('otp')}
								/>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="password">Mật khẩu mới</FormLabel>
								<TextField
									id="password"
									name="password"
									type={showPassword ? 'text' : 'password'}
									autoComplete="new-password"
									required
									fullWidth
									value={formData.password}
									onChange={handleChange('password')}
									error={!!fieldError('password')}
									helperText={fieldError('password') ?? `Tối thiểu ${PASSWORD_MIN_LENGTH} ký tự`}
									InputProps={{ endAdornment: passwordAdornment }}
								/>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="confirmPassword">Nhập lại mật khẩu mới</FormLabel>
								<TextField
									id="confirmPassword"
									name="confirmPassword"
									type={showPassword ? 'text' : 'password'}
									autoComplete="new-password"
									required
									fullWidth
									value={formData.confirmPassword}
									onChange={handleChange('confirmPassword')}
									error={!!fieldError('confirmPassword')}
									helperText={fieldError('confirmPassword')}
								/>
							</FormControl>
							<Button type="submit" variant="contained" fullWidth disabled={resetMutation.isPending}>
								{resetMutation.isPending ? 'Đang xử lý...' : 'Đặt lại mật khẩu'}
							</Button>
							<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
								<Link component={RouterLink} to="/login" variant="body2">
									Quay lại đăng nhập
								</Link>
								<Link component={RouterLink} to="/forgot-password" variant="body2">
									Gửi lại mã
								</Link>
							</Box>
						</Box>
					)}
				</Card>
			</SignInContainer>
		</>
	);
}
